import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent } from 'cms/entries/entries';
import { generatePresetPictureSet } from 'components/shared/performance/pictures';
import type { ILpComponentBasicPageHeader } from 'declarations/contentful';
import { optimizeContentfulImageUrl } from 'helpers/imageUrl';

import ContextualField from './ContextualField';
import { LPLink } from './shared/Buttons';
import Container from './shared/Container';
import ExtraCtas from './shared/ExtraCtas';
import { HERO_IMAGE_DESKTOP_PROPS, HERO_IMAGE_MOBILE_PROPS } from './shared/hero/heroConstants';
import {
  BNColorThemes,
  getBNComponentThemeColors,
  getComponentThemeColors,
} from './shared/styles/ComponentTheme';
import { Desc, Eyebrow } from './shared/Typography';
import { contentfulToImagesWorkerUrl, markdownToReactElement } from './shared/utils';

// The shared theme helper still returns Bricks color tokens (it's also consumed
// by the Bricks-based Typography components below). Map the tokens this file
// renders through Atoms primitives to semantic Atoms utilities. The class
// strings are literals so Tailwind generates them despite the dynamic lookup.
const BG_CLASS: Record<string, string> = {
  $backgroundPrimary: 'bg-od-bg-primary',
  $backgroundInversePrimary: 'bg-od-bg-inverse-primary',
  $backgroundTertiary: 'bg-od-bg-tertiary',
};
const ACCENT_TEXT_CLASS: Record<string, string> = {
  $contentTertiary: 'text-od-content-tertiary',
};

// Headline color: the Bricks Typography components (Eyebrow/Desc) still consume
// the token form, the Atoms-rendered headline consumes the utility form.
const FOREGROUND_TOKEN = {
  Light: '$contentInversePrimary',
  Dark: '$contentPrimary',
} as const;
const FOREGROUND_TEXT_CLASS = {
  Light: 'text-od-content-inverse-primary',
  Dark: 'text-od-content-primary',
} as const;

const RenderBasicPageHeader = (entry: ILpComponentBasicPageHeader) => {
  const { fields } = entry;
  // TODO: Remove the 'componentTheme' variable once all components using it are migrated to bricks-next.
  const componentTheme = fields.backgroundColor
    ? getComponentThemeColors(fields.backgroundColor)
    : getComponentThemeColors('White 0');

  const theme = getBNComponentThemeColors(
    fields.backgroundColor in BNColorThemes
      ? (fields.backgroundColor as BNColorThemes)
      : BNColorThemes.Light,
  );

  const foreground = fields.foregroundColor || 'Dark';

  const imagePosition = fields.imagePosition || 'None';
  const bgImage =
    imagePosition == 'Background' && fields.image
      ? optimizeContentfulImageUrl(fields.image.fields.file.url, { width: 2400, quality: 80 })
      : '';
  const noImage = fields.image === null || fields.image === undefined;
  const headlineLength = fields?.title?.length || 0;
  const longHeadline = headlineLength > 20;
  const isLargeLayout = fields.layout === 'Large';
  const customCTAconfig = fields.customCtaConfig;

  const showCustomCTAButton = fields.customCtaButton,
    showDefaultCTALink =
      !showCustomCTAButton && (fields.ctaDisplayText || fields.ctaUrl || fields.extraCtas);

  const showAppStoreLinks = showCustomCTAButton && customCTAconfig?.buttonStyle === 'appLinks';

  const appleStoreLink = customCTAconfig?.appStoreLink as string,
    googlePlayStoreLink = customCTAconfig?.playStoreLink as string;

  // Long headlines step down one typography size; Large titles always use the
  // display ramp. Title and accent share the same ramp.
  const headlineTypographyClass =
    fields.titleSize === 'Large' || !longHeadline
      ? 'text-header-md md:text-display-sm'
      : 'text-header-sm md:text-header-md';

  return (
    <Col
      className={cn(
        'lp-section relative',
        BG_CLASS[theme.bgPrimary as string] ?? 'bg-od-bg-primary',
      )}
    >
      {bgImage && (
        <Image
          src={bgImage}
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchPriority="high"
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        />
      )}
      <Col className="relative z-10">
        <Container
          noSectionSpacing
          className={
            fields.titleSize === 'Large'
              ? 'px-6 py-25 md:px-10 md:py-16'
              : 'px-6 py-22 md:px-10 md:py-14'
          }
        >
          {fields.title && (
            <Col
              className={cn(
                'justify-center gap-12',
                !noImage && 'min-h-128',
                !(isLargeLayout || noImage) && 'lg:max-h-186 lg:min-h-108',
                !isLargeLayout && 'lg:flex-row',
              )}
            >
              <Col
                className={cn(
                  'justify-center gap-6',
                  isLargeLayout ? 'lg:grow lg:basis-auto lg:items-center' : 'lg:flex-1',
                )}
              >
                {fields.eyebrow && (
                  <Eyebrow
                    color={FOREGROUND_TOKEN[foreground]}
                    size={fields.eyebrowSize === 'Large' ? '$bodyXlarge' : '$bodySmall'}
                  >
                    {fields.eyebrow}
                  </Eyebrow>
                )}
                <Col className="gap-8">
                  <Col>
                    <Text
                      as="h1"
                      className={cn(
                        headlineTypographyClass,
                        FOREGROUND_TEXT_CLASS[foreground],
                        isLargeLayout && 'lg:text-center',
                      )}
                    >
                      {markdownToReactElement(fields.title || '')}
                    </Text>
                    {fields.titleAccent && (
                      <Text
                        as="span"
                        className={cn(
                          headlineTypographyClass,
                          ACCENT_TEXT_CLASS[theme.contentPrimaryAccent as string] ??
                            'text-od-content-tertiary',
                          isLargeLayout && 'lg:text-center',
                        )}
                      >
                        {markdownToReactElement(fields.titleAccent || '')}
                      </Text>
                    )}
                  </Col>
                  {fields.desc && (
                    <Box className="block">
                      <Desc
                        color={FOREGROUND_TOKEN[foreground]}
                        $largerThanMD={{
                          textAlign: isLargeLayout ? 'center' : 'unset',
                          marginRight: isLargeLayout ? 0 : '$16x',
                        }}
                      >
                        {fields.desc}
                      </Desc>
                    </Box>
                  )}
                  {fields.contextualField && (
                    <Row
                      className={cn(
                        'mt-6 w-full items-start',
                        isLargeLayout && 'lg:items-center lg:justify-center',
                      )}
                    >
                      <ContextualField
                        theme={componentTheme}
                        contextFields={fields.contextualField}
                        inputLocation="basic-page-header"
                      />
                    </Row>
                  )}
                  {showDefaultCTALink && (
                    <Row
                      className={cn(
                        'mt-8 flex-wrap items-center gap-4',
                        isLargeLayout && 'lg:justify-center',
                      )}
                    >
                      {fields.ctaDisplayText && fields.ctaUrl && (
                        <LPLink
                          aria-label={`Go to page ${fields?.ctaDisplayText}`}
                          analyticsName={`cosmos-landing-page-basic-page-header-${fields?.analyticsId}`}
                          ctaStyle={fields.ctaStyle}
                          ctaDisplayText={fields.ctaDisplayText}
                          ctaUrl={fields.ctaUrl}
                        />
                      )}
                      {fields.extraCtas && <ExtraCtas ctas={fields.extraCtas} />}
                    </Row>
                  )}

                  {showAppStoreLinks && (
                    <Col
                      className={cn(
                        'mt-4 w-full justify-between gap-2 rounded-2xl bg-od-bg-tertiary p-3 md:flex-row',
                        isLargeLayout ? 'lg:max-w-full' : 'lg:max-w-[80%]',
                      )}
                    >
                      <Text as="p" className="min-w-max text-body-sm font-medium">
                        Get the app
                      </Text>

                      <Row className="flex-wrap gap-2">
                        <Box
                          href={appleStoreLink}
                          external
                          aria-label="Opendoor Applestore button"
                          analyticsName="cosmos-landing-page-v2-header-cta-applestore-link"
                        >
                          <Image
                            src="https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078078-95093.png?preset=square-2048"
                            alt="Opendoor Applestore"
                            width={135}
                            height={45}
                          />
                        </Box>
                        <Box
                          href={googlePlayStoreLink}
                          external
                          aria-label="Opendoor Google play button"
                          analyticsName="cosmos-landing-page-v2-header-cta-google-playstore-link"
                        >
                          <Image
                            src="https://images.opendoor.com/source/s3/imgdrop-production/2023-12-1702502078081-28903.png?preset=square-2048"
                            alt="Opendoor Google Play"
                            width={135}
                            height={45}
                          />
                        </Box>
                      </Row>
                    </Col>
                  )}
                </Col>
              </Col>
              {fields.image && imagePosition === 'Right' && (
                <>
                  {/* Desktop hero image */}
                  <Box
                    className={cn(
                      'hidden justify-center overflow-hidden rounded-4xl md:block lg:flex lg:max-h-186 lg:min-h-108',
                      isLargeLayout ? 'lg:grow lg:basis-auto' : 'lg:flex-1',
                    )}
                  >
                    {generatePresetPictureSet({
                      ...HERO_IMAGE_DESKTOP_PROPS,
                      photoUrl: contentfulToImagesWorkerUrl(fields.image.fields.file.url),
                    })}
                  </Box>
                  {/* Mobile hero image */}
                  <Box className="block justify-center overflow-hidden rounded-4xl md:hidden">
                    {generatePresetPictureSet({
                      ...HERO_IMAGE_MOBILE_PROPS,
                      photoUrl: contentfulToImagesWorkerUrl(fields.image.fields.file.url),
                    })}
                  </Box>
                </>
              )}
            </Col>
          )}
        </Container>
      </Col>
    </Col>
  );
};

const BasicPageHeader: EntryComponent<ILpComponentBasicPageHeader> = {
  render: RenderBasicPageHeader,
};

export default BasicPageHeader;
